/**
 * Reading the one refusal that is an ANSWER, not a failure (PR-8, owner
 * decision D10a).
 *
 * A suggestion built by an older materialiser is refused at accept time with
 * **409** and
 * `detail: { kind: "stale_suggestion", message, notice, … }`. That is not an
 * error to apologise for: the coach rebuilds and reviews again, which is one
 * click. Treating it as a generic failure (the only thing a `catch` can do
 * without looking inside) would hide the action.
 *
 * Kept out of the service layer on purpose. `acceptCompiledRules` deliberately
 * does NOT swallow its errors — the panel needs the distinction, so the panel's
 * helper is where the body is inspected, and this module is pure and testable
 * without a network stub.
 *
 * An ABSENT version stamp counts as version 0 and is refused (plan N-7) — that
 * decision is the backend's; all this reads is the verdict.
 */

/** The shape we look for, read defensively — this is an unknown error object. */
interface StaleDetailShape {
  kind?: unknown;
  message?: unknown;
}

function readDetail(err: unknown): StaleDetailShape | null {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== 'object') return null;
  const detail = (data as { detail?: unknown }).detail;
  if (!detail || typeof detail !== 'object') return null;
  return detail as StaleDetailShape;
}

/**
 * Is this error the stale-suggestion refusal, and what did the server say?
 *
 * Returns:
 *  · the server's own `message` when it sent one — the coach reads the
 *    backend's words, which is the pattern `materializeSuggestions` already
 *    uses for the same 409 on the materialise path;
 *  · `''` when it is the stale refusal with no message, so the caller can fall
 *    back to its own prompt;
 *  · `null` when this is some other error entirely.
 *
 * The empty-string-vs-null distinction is the whole point: `null` means "not
 * this case", and `''` means "this case, no words" — collapsing them would
 * turn a rebuild prompt into a generic failure.
 */
export function readStaleSuggestionMessage(err: unknown): string | null {
  const detail = readDetail(err);
  if (!detail || detail.kind !== 'stale_suggestion') return null;
  return typeof detail.message === 'string' ? detail.message : '';
}
